// Advanced pricing logic composable
import { PERIODS, PERIOD_MULTIPLIERS, PERIOD_SAVED_MONTHS, PERIOD_OFFERS } from "@config/pricingConfig"
import { EXTRA_COSTS_YEARLY, UPGRADE_THRESHOLD } from "@data/extraCosts"
import { plans } from "@data/plans"
import { needsCustomPlan } from "@/constants/customPlan"

export function usePricingAdvanced() {
  /**
   * Calculate plan based on advanced pricing logic with extra costs
   * @param {Object} pricingState - Current pricing state
   * @returns {Object} - Calculated plan result
   */
  function calculatePlan(pricingState) {
    let finalPlan = null
    let displayPrice = 0
    let priceLabel = "/mois"
    let isCustomPlan = false
    let savingsBadge = null
    let offerText = null

    // Check if custom plan is needed (values exceed maximums)
    if (needsCustomPlan(pricingState)) {
      const customPlan = plans.find((plan) => plan.id === "custom")
      return {
        plan: customPlan,
        finalPrice: 0,
        priceLabel: "",
        savingsBadge: null,
        offerText: null,
        displayValues: {
          socialAccounts: pricingState.socialAccounts,
          users: pricingState.users,
          sources: pricingState.sources,
        },
        extraCosts: {
          social: null,
          users: null,
          sources: null,
        },
        isCustomPlan: true,
        features: customPlan.features,
        period: pricingState.period,
      }
    }

    // Filter out custom plan for normal plan search
    const regularPlans = plans.filter((plan) => plan.id !== "custom")

    // Monthly period (simple tiers)
    if (pricingState.period === PERIODS.MONTHLY) {
      // Find the first plan that covers ALL current needs
      finalPlan = regularPlans.find(
        (p) =>
          p.maxSocialAccounts >= pricingState.socialAccounts &&
          p.maxUsers >= pricingState.users &&
          p.maxSources >= pricingState.sources
      )

      // Safety fallback if needs exceed the highest plan
      if (!finalPlan) finalPlan = regularPlans[regularPlans.length - 1] // Get premium

      displayPrice = finalPlan.priceMonthly
      priceLabel = "/mois"
    } else {
      // Yearly / 2-year period (custom pricing with extra costs)
      priceLabel = ""

      // Price multiplier (10 months paid for 1 year, 18 months for 2 years)
      const multiplier = PERIOD_MULTIPLIERS[pricingState.period]

      // Find the "Ceiling" plan that covers everything natively
      let ceilingPlanIndex = regularPlans.findIndex(
        (p) =>
          p.maxSocialAccounts >= pricingState.socialAccounts &&
          p.maxUsers >= pricingState.users &&
          p.maxSources >= pricingState.sources
      )
      if (ceilingPlanIndex === -1) ceilingPlanIndex = regularPlans.length - 1 // Get premium
      const ceilingPlan = regularPlans[ceilingPlanIndex]

      // Compare Ceiling Plan vs Lower Plan + Options
      if (ceilingPlanIndex === 0) {
        // Already on the lowest plan (Freelance), no comparison possible
        finalPlan = ceilingPlan
        displayPrice = ceilingPlan.priceMonthly * multiplier
      } else {
        // The base plan for custom calculation is the one right below the ceiling
        const basePlan = regularPlans[ceilingPlanIndex - 1]

        // Calculate ceiling scenario total yearly price
        const ceilingTotalYearly = ceilingPlan.priceMonthly * multiplier

        // Calculate custom scenario (Base + Yearly Options)
        const extraSocial = Math.max(
          0,
          pricingState.socialAccounts - basePlan.maxSocialAccounts
        )
        const extraUsers = Math.max(0, pricingState.users - basePlan.maxUsers)
        const extraSources = Math.max(0, pricingState.sources - basePlan.maxSources)

        const optionsTotalYearly =
          extraSocial * EXTRA_COSTS_YEARLY.social +
          extraUsers * EXTRA_COSTS_YEARLY.users +
          extraSources * EXTRA_COSTS_YEARLY.sources

        const customTotalYearly =
          basePlan.priceMonthly * multiplier + optionsTotalYearly

        // Decision based on threshold and special freelance rules
        const forceUpgradeToStarter =
          basePlan.id === "freelance" &&
          pricingState.socialAccounts >= ceilingPlan.maxSocialAccounts &&
          pricingState.users >= ceilingPlan.maxUsers &&
          pricingState.sources >= ceilingPlan.maxSources

        if (
          !forceUpgradeToStarter &&
          customTotalYearly <= ceilingTotalYearly - UPGRADE_THRESHOLD
        ) {
          // Use base plan with custom pricing
          finalPlan = basePlan
          displayPrice = customTotalYearly
          isCustomPlan = true
        } else {
          // Use ceiling plan (forced upgrade or economical choice)
          finalPlan = ceilingPlan
          displayPrice = ceilingTotalYearly
          isCustomPlan = false
        }
      }

      // Set promotional badges AFTER finalPlan is determined
      if (pricingState.period === PERIODS.ONE_YEAR) {
        // savingsBadge = "Économisez 17%" // Keep for potential % display
        savingsBadge = `Économisez ${finalPlan.priceMonthly * PERIOD_SAVED_MONTHS[PERIODS.ONE_YEAR]}€ HT`
        offerText = PERIOD_OFFERS[PERIODS.ONE_YEAR]
      } else {
        // savingsBadge = "Économisez 25%" // Keep for potential % display
        savingsBadge = `Économisez ${finalPlan.priceMonthly * PERIOD_SAVED_MONTHS[PERIODS.TWO_YEARS]}€ HT`
        offerText = PERIOD_OFFERS[PERIODS.TWO_YEARS]
      }
    }

    // Calculate display values (always show user-selected values unless plan offers more)
    const displayValues = {
      socialAccounts: Math.max(
        finalPlan.maxSocialAccounts,
        pricingState.socialAccounts
      ),
      users: Math.max(finalPlan.maxUsers, pricingState.users),
      sources: Math.max(finalPlan.maxSources, pricingState.sources),
    }

    // Calculate extra costs display
    const extraCosts = {
      social: null,
      users: null,
      sources: null,
    }

    // Show extra costs in yearly modes or when using custom plan
    if (pricingState.period !== PERIODS.MONTHLY) {
      if (isCustomPlan) {
        // Show extra cost texts only for values exceeding plan limits
        if (pricingState.socialAccounts > finalPlan.maxSocialAccounts) {
          extraCosts.social = ` (+${EXTRA_COSTS_YEARLY.social}€/page)`
        }
        if (pricingState.users > finalPlan.maxUsers) {
          extraCosts.users = ` (+${EXTRA_COSTS_YEARLY.users}€/user)`
        }
        if (pricingState.sources > finalPlan.maxSources) {
          extraCosts.sources = ` (+${EXTRA_COSTS_YEARLY.sources}€/source)`
        }
      } else {
        // Always show extra cost information in yearly mode
        extraCosts.social = ` (+${EXTRA_COSTS_YEARLY.social}€/page)`
        extraCosts.users = ` (+${EXTRA_COSTS_YEARLY.users}€/user)`
        extraCosts.sources = ` (+${EXTRA_COSTS_YEARLY.sources}€/source)`
      }
    }

    return {
      plan: finalPlan,
      finalPrice: Math.round(displayPrice),
      priceLabel,
      savingsBadge,
      offerText,
      displayValues,
      extraCosts,
      isCustomPlan,
      features: finalPlan.features,
      period: pricingState.period,
    }
  }

  return {
    calculatePlan,
  }
}
